import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import type { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from '@openng/optimus-ui/autocomplete';
import { CampaignService } from '../../../../services/campaign.service';
import { ICampaign, ICampaignPhase } from '../../../../models/campaign/campaign.interface';

export interface NameCampaignDialogData {
  channelLabel: string;
}

export interface NameCampaignDialogResult {
  /** Present only when the typed name matched an existing campaign the user picked from the autocomplete. */
  campaignId?: string;
  campaignName: string;
  /** The picked existing campaign's phases, if any - lets the caller skip a round-trip to re-fetch them. */
  phases?: ICampaignPhase[];
}

/**
 * "Name this <Channel> campaign" prompt shown on Save when a template doesn't
 * belong to any campaign yet (RCA-480). Visual/interaction spec matches the
 * ContentRocketWebpage_v16 mockup's save-name dialog, but - unlike the
 * mockup, which just names a local, unrelated record - the caller wires a
 * typed name to the real createCampaign/editTemplateToPhaseConnections
 * backend calls. The name field autocompletes against existing campaigns so
 * a name that already exists gets offered instead of creating a duplicate.
 * Closing with undefined (Cancel or the X) aborts the save entirely.
 */
@Component({
  selector: 'app-name-campaign-dialog',
  imports: [NgIf, FormsModule, AutoCompleteModule],
  templateUrl: './name-campaign-dialog.component.html',
  styleUrl: './name-campaign-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NameCampaignDialogComponent {
  campaignName = '';
  errorMessage = '';
  suggestions: ICampaign[] = [];
  private selectedExisting: ICampaign | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NameCampaignDialogData,
    private dialogRef: MatDialogRef<NameCampaignDialogComponent, NameCampaignDialogResult | undefined>,
    private campaignService: CampaignService
  ) {
  }

  search(event: AutoCompleteCompleteEvent): void {
    this.campaignService.getCampaignList({ first: 0, rows: 20, search: event.query }).subscribe({
      next: (campaigns) => this.suggestions = campaigns,
      error: () => this.suggestions = [],
    });
  }

  onNameChange(value: string): void {
    this.campaignName = value;
    // Typing away from a previously-picked suggestion means we're back to "new name" territory.
    if (this.selectedExisting && this.selectedExisting.campaignName !== value) {
      this.selectedExisting = null;
    }
    this.errorMessage = '';
  }

  onSelectExisting(event: AutoCompleteSelectEvent): void {
    this.selectedExisting = event.value as ICampaign;
    this.campaignName = this.selectedExisting.campaignName;
  }

  save(): void {
    const trimmed = this.campaignName.trim();
    if (!trimmed) {
      this.errorMessage = 'Campaign name is required';
      return;
    }

    if (this.selectedExisting && this.selectedExisting.campaignName === trimmed) {
      this.dialogRef.close({
        campaignId: this.selectedExisting.campaignId,
        campaignName: trimmed,
        phases: this.selectedExisting.phases,
      });
      return;
    }

    this.dialogRef.close({ campaignName: trimmed });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
